/**
 * Rasterize a Recharts <svg> to a PNG and download it. No external deps:
 * the SVG is serialized, drawn onto a 2x canvas over a solid background
 * (Recharts uses presentation attributes, so styles survive serialization),
 * then exported as a PNG blob.
 *
 * CSV export reuses the shared `exportTableToCSV` from `~/ui-rtk/utils/csv-export`;
 * this file only covers the chart-image case, for which there is no shared util.
 */
export async function downloadSvgAsPng(
  svg: SVGSVGElement,
  filename: string,
  background = "#0b1220",
) {
  const rect = svg.getBoundingClientRect()
  const width = Math.ceil(rect.width) || 800
  const height = Math.ceil(rect.height) || 320

  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute("width", String(width))
  clone.setAttribute("height", String(height))
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg")

  const xml = new XMLSerializer().serializeToString(clone)
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`

  const img = new Image()
  img.width = width
  img.height = height
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error("Failed to rasterize chart SVG"))
    img.src = src
  })

  const scale = 2
  const canvas = document.createElement("canvas")
  canvas.width = width * scale
  canvas.height = height * scale
  const ctx = canvas.getContext("2d")
  if (!ctx) return
  ctx.scale(scale, scale)
  ctx.fillStyle = background
  ctx.fillRect(0, 0, width, height)
  ctx.drawImage(img, 0, 0, width, height)

  await new Promise<void>(resolve => {
    canvas.toBlob(blob => {
      if (blob) {
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = filename.endsWith(".png")
          ? filename
          : `${filename}.png`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        URL.revokeObjectURL(url)
      }
      resolve()
    }, "image/png")
  })
}
